import { useState, useMemo, useEffect } from 'react';
import { useZborovi } from '../hooks/useZborovi';
import { RegionFilter } from '../types/zbor';

export function Zborovi() {
  const { zborovi, isLoading, error } = useZborovi();
  const [regionFilter, setRegionFilter] = useState<RegionFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);

  const regions: RegionFilter[] = ['all', 'Beograd', 'Vojvodina', 'Centralna Srbija', 'Zapadna Srbija', 'Istočna Srbija', 'Južna Srbija', 'Ostalo'];

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showAddForm) {
        setShowAddForm(false);
      }
    };

    if (showAddForm) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [showAddForm]);

  const filteredZborovi = useMemo(() => {
    return zborovi.filter(zbor => {
      const matchesRegion = regionFilter === 'all' || zbor.regionMain === regionFilter;
      const matchesSearch = searchQuery === '' || 
        zbor.mesto.toLowerCase().includes(searchQuery.toLowerCase()) ||
        zbor.grad.toLowerCase().includes(searchQuery.toLowerCase());
      
      return matchesRegion && matchesSearch;
    });
  }, [zborovi, regionFilter, searchQuery]);

  const groupedZborovi = useMemo(() => {
    const groups: Record<string, typeof filteredZborovi> = {};
    
    filteredZborovi.forEach(zbor => {
      const key = zbor.grad;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(zbor);
    });
    
    return groups;
  }, [filteredZborovi]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-600">Učitavanje...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-red-600">Greška: {error}</div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50 flex flex-col">
      {/* Filters */}
      <div className="bg-white border-b p-3 md:p-4 space-y-3 md:space-y-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <h2 className="text-lg md:text-xl font-semibold">Pronađi svoj zbor</h2>
            <button
              onClick={() => setShowAddForm(true)}
              className="inline-flex items-center gap-1 md:gap-2 px-2 md:px-4 py-1 md:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs md:text-sm font-medium"
            >
              <svg className="w-3 h-3 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="hidden sm:inline">Dodaj novi zbor</span>
              <span className="sm:hidden">Dodaj</span>
            </button>
          </div>
          
          {/* Search */}
          <div className="mb-3 md:mb-4">
            <input
              type="text"
              placeholder="Pretraži po mestu ili gradu..."
              className="w-full md:w-96 px-3 py-2 text-sm md:text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          {/* Region filters - Much more compact on mobile */}
          <div className="grid grid-cols-3 md:flex md:flex-wrap gap-1 md:gap-2">
            {regions.map(region => (
              <button
                key={region}
                onClick={() => setRegionFilter(region)}
                className={`px-2 py-1 md:px-3 md:py-2 rounded-full text-xs font-medium transition-colors text-center ${
                  regionFilter === region
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {region === 'all' ? 'Svi' : 
                 region === 'Centralna Srbija' ? 'Centralna' :
                 region === 'Zapadna Srbija' ? 'Zapadna' :
                 region === 'Istočna Srbija' ? 'Istočna' :
                 region === 'Južna Srbija' ? 'Južna' :
                 region}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="flex-1 overflow-auto p-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-sm text-gray-600 mb-4">
            Pronađeno {filteredZborovi.length} zborova
          </div>

          {Object.entries(groupedZborovi).sort().map(([grad, zboroviInGrad]) => (
            <div key={grad} className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                <svg className="w-5 h-5 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {grad}
              </h3>
              
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {zboroviInGrad.map((zbor, index) => (
                  <div key={index} className="bg-white rounded-lg shadow-sm border p-4 hover:shadow-md transition-shadow">
                    <div className="space-y-3">
                      {/* Location info */}
                      <div>
                        <div className="font-medium text-gray-900">{zbor.mesto || grad}</div>
                        <div className="text-sm text-gray-600">{zbor.regionMain}</div>
                      </div>
                      
                      {/* Links - only show if they exist */}
                      {(zbor.instagram || zbor.chat) && (
                        <div className="flex gap-2 flex-wrap">
                          {zbor.instagram && (
                            <a
                              href={zbor.instagram}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 px-3 py-2 bg-white hover:bg-gray-50 text-gray-800 border border-gray-300 rounded-md text-sm font-medium transition-colors"
                            >
                              <img 
                                src="/colored-instagram-logo-new.png" 
                                alt="Instagram" 
                                className="w-4 h-4"
                              />
                              Instagram
                            </a>
                          )}
                          
                          {zbor.chat && (
                            <a
                              href={zbor.chat}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 px-3 py-2 bg-white hover:bg-gray-50 text-gray-800 border border-gray-300 rounded-md text-sm font-medium transition-colors"
                            >
                              {zbor.chat.includes('viber') ? (
                                <img 
                                  src="/viber-icon-logo.png" 
                                  alt="Viber" 
                                  className="w-6 h-6"
                                />
                              ) : (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                </svg>
                              )}
                              {zbor.chat.includes('viber') ? 'Viber' : 
                               zbor.chat.includes('whatsapp') ? 'WhatsApp' : 
                               zbor.chat.includes('t.me') ? 'Telegram' : 'Chat'}
                            </a>
                          )}
                        </div>
                      )}
                      
                      {/* Show message if no links available */}
                      {!zbor.instagram && !zbor.chat && (
                        <div className="text-sm text-gray-500 italic">
                          Kontakt informacije nisu dostupne
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {filteredZborovi.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-500">Nema rezultata za zadati filter</div>
            </div>
          )}
        </div>
      </div>

      {/* Add New Zbor Modal */}
      {showAddForm && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setShowAddForm(false)}
        >
          <div 
            className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Dodaj novi zbor</h3>
              <button
                onClick={() => setShowAddForm(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="overflow-auto max-h-[calc(90vh-80px)]">
              <iframe 
                src="https://docs.google.com/forms/d/e/1FAIpQLSeMQun45juaJGV2OKxN1KhiIy7wxnr5PcfjgtoOtkZC8Xk-mQ/viewform?embedded=true" 
                width="100%" 
                height="1334" 
                frameBorder="0" 
                marginHeight={0} 
                marginWidth={0}
                className="w-full"
              >
                Loading…
              </iframe>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 